import { TGE, TeamMember, Prediction, LeaderboardEntry, GameStats } from '../types';

// Sample TGE data with correct project names and dates
export const sampleTGEs: TGE[] = [
      {
      id: '1',
      name: 'Unite',
      description: '',
      launchDate: '2025-07-30',
      predictionCloseDate: '2025-07-29T23:00:00',
      expectedFDV: 50000000,
      status: 'OPEN',
      totalBets: 0,
      minBet: 10,
      maxBet: 50,
      timeUntilClose: '2 days'
    },
    {
      id: '2',
      name: 'RHEA',
      description: '',
      launchDate: '2025-07-30',
      predictionCloseDate: '2025-07-29T23:00:00',
      expectedFDV: 45000000,
      status: 'OPEN',
      totalBets: 0,
      minBet: 10,
      maxBet: 50,
      timeUntilClose: '2 days'
    },
    {
      id: '3',
      name: 'Towns',
      description: '',
      launchDate: '2025-08-05',
      predictionCloseDate: '2025-08-04T23:00:00',
      expectedFDV: 55000000,
      status: 'OPEN',
      totalBets: 0,
      minBet: 10,
      maxBet: 50,
      timeUntilClose: '8 days'
    },
    {
      id: '4',
      name: 'Caesar',
      description: '',
      launchDate: '2025-08-16',
      predictionCloseDate: '2025-08-15T23:00:00',
      expectedFDV: 30000000,
      status: 'OPEN',
      totalBets: 0,
      minBet: 10,
      maxBet: 50,
      timeUntilClose: '19 days'
    },
  {
    id: '5',
    name: 'Rivalz',
    description: '',
    launchDate: '2025-01-01', // TBD - placeholder date
    predictionCloseDate: '2025-01-01', // TBD - placeholder date
    expectedFDV: 40000000,
    status: 'OPEN',
    totalBets: 0,
    minBet: 10,
    maxBet: 50,
    timeUntilClose: 'TBD'
  },
  {
    id: '6',
    name: 'OpenLedger',
    description: '',
    launchDate: '2025-01-01', // TBD - placeholder date
    predictionCloseDate: '2025-01-01', // TBD - placeholder date
    expectedFDV: 35000000,
    status: 'OPEN',
    totalBets: 0,
    minBet: 10,
    maxBet: 50,
    timeUntilClose: 'TBD'
  },
  {
    id: '7',
    name: 'Gaia',
    description: '',
    launchDate: '2025-01-01', // TBD - placeholder date
    predictionCloseDate: '2025-01-01', // TBD - placeholder date
    expectedFDV: 35000000,
    status: 'OPEN',
    totalBets: 0,
    minBet: 10,
    maxBet: 50,
    timeUntilClose: 'TBD'
  }
];

// Team members with starting chips
export const teamMembers: TeamMember[] = [
  { id: '1', name: 'Vicki', chips: 100, totalPoints: 0, predictions: [] },
  { id: '2', name: 'Dan', chips: 100, totalPoints: 0, predictions: [] },
  { id: '3', name: 'Francis', chips: 100, totalPoints: 0, predictions: [] },
  { id: '4', name: 'Matt', chips: 100, totalPoints: 0, predictions: [] },
  { id: '5', name: 'Marcou', chips: 100, totalPoints: 0, predictions: [] },
  { id: '6', name: 'Noah', chips: 100, totalPoints: 0, predictions: [] },
  { id: '7', name: 'Andrew', chips: 100, totalPoints: 0, predictions: [] },
  { id: '8', name: 'Nitin', chips: 100, totalPoints: 0, predictions: [] }
];

// Calculate points based on prediction accuracy
export const calculatePoints = (predictedFDV: number, actualFDV: number, chipsBet: number): number => {
  const accuracy = Math.max(0, 1 - Math.abs(predictedFDV - actualFDV) / actualFDV);
  const basePoints = Math.round(accuracy * 100);
  const multiplier = chipsBet / 10; // More chips = more points
  return Math.round(basePoints * multiplier);
};

// Calculate team participation for a TGE
export const calculateTeamParticipation = (tgeId: string, predictions: Prediction[]): { count: number; percentage: number } => {
  const tgePredictions = predictions.filter(p => p.tgeId === tgeId);
  const uniqueMembers = new Set(tgePredictions.map(p => p.teamMemberId));
  const percentage = Math.round((uniqueMembers.size / teamMembers.length) * 100);
  return { count: uniqueMembers.size, percentage };
};

// Generate leaderboard data
export const generateLeaderboard = (predictions: Prediction[], teamMembers: TeamMember[]): LeaderboardEntry[] => {
  const memberStats = new Map<string, { points: number; predictions: number; accuracy: number; chips: number }>();

  // Initialize stats for all members from the provided teamMembers array
  teamMembers.forEach(member => {
    memberStats.set(member.id, { points: 0, predictions: 0, accuracy: 0, chips: member.chips });
  });

  // Calculate stats from predictions
  predictions.forEach(prediction => {
    const stats = memberStats.get(prediction.teamMemberId);
    if (stats) {
      stats.predictions++;
      if (prediction.points) {
        stats.points += prediction.points;
      }
    }
  });

  // Calculate average accuracy (simplified)
  memberStats.forEach((stats, memberId) => {
    stats.accuracy = stats.predictions > 0 ? Math.round(stats.points / stats.predictions) : 0;
  });

  return teamMembers.map(member => {
    const stats = memberStats.get(member.id) || { points: 0, predictions: 0, accuracy: 0, chips: member.chips };
    return {
      teamMemberId: member.id,
      name: member.name,
      totalPoints: stats.points,
      chipsRemaining: stats.chips,
      predictionsCount: stats.predictions,
      accuracy: stats.accuracy
    };
  }).sort((a, b) => b.totalPoints - a.totalPoints || b.chipsRemaining - a.chipsRemaining);
};

// Calculate game statistics
export const calculateGameStats = (tges: TGE[], predictions: Prediction[]): GameStats => {
  const totalTGEs = tges.length;
  const activeTGEs = tges.filter(tge => tge.status === 'OPEN').length;
  const totalPredictions = predictions.length;
  const totalChipsInPlay = predictions.reduce((sum, pred) => sum + pred.chipsBet, 0);
  const averageAccuracy = predictions.length > 0 
    ? Math.round(predictions.reduce((sum, pred) => sum + (pred.points || 0), 0) / predictions.length)
    : 0;

  return {
    totalTGEs,
    activeTGEs,
    totalPredictions,
    totalChipsInPlay,
    averageAccuracy
  };
}; 